import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Center,
  Container,
  Group,
  Paper,
  SegmentedControl,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { LineChart } from "@mantine/charts";
import { IconSearch, IconInfoCircle, IconTrendingUp } from "@tabler/icons-react";
import { useLocation } from "wouter";
import BrandGuard from "../_Shared/BrandGuard";
import BeaconLoader from "../_Shared/BeaconLoader";
import { jsonGet } from "../api";

const RANGES = [
  { label: "28 days", value: "28" },
  { label: "90 days", value: "90" },
  { label: "6 months", value: "180" },
  { label: "16 months", value: "500" },
];

/** Chart views. Clicks and impressions share a chart; position is a rank on its own inverted axis. */
const VIEWS = {
  traffic: {
    label: "Clicks & impressions",
    series: [
      { name: "clicks", label: "Clicks", color: "indigo.6" },
      { name: "impressions", label: "Impressions", color: "teal.6" },
    ],
  },
  position: {
    label: "Avg position",
    series: [{ name: "position", label: "Avg position", color: "orange.6" }],
    reversed: true,
  },
};

/** Rank 0 is missing data, not the top of page one. */
function reportedPosition(row) {
  return row?.impressions > 0 && row?.position > 0 ? row.position : null;
}

function formatPosition(value) {
  return value > 0 ? Number(value).toFixed(1) : "—";
}

export default function SearchConsolePage() {
  return (
    <BrandGuard>
      {({ brand }) => <SearchConsoleContent brand={brand} />}
    </BrandGuard>
  );
}

function SearchConsoleContent({ brand }) {
  const [, setLocation] = useLocation();
  const [days, setDays] = useState("90");
  const [view, setView] = useState("traffic");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [keywords, setKeywords] = useState(null);
  const [selectedKeyword, setSelectedKeyword] = useState(null);
  const [movers, setMovers] = useState(null);

  useEffect(() => {
    if (!brand?.id || !brand.gscSiteUrl) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const query = selectedKeyword ? `&keyword=${encodeURIComponent(selectedKeyword)}` : "";
    jsonGet(`google-search-console/brands/${brand.id}/daily?days=${days}${query}`)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [brand?.id, brand?.gscSiteUrl, days, selectedKeyword]);

  useEffect(() => {
    if (!brand?.id || !brand.gscSiteUrl) return;
    jsonGet(`google-search-console/brands/${brand.id}/keywords`)
      .then(setKeywords)
      .catch(() => setKeywords(null));
    jsonGet(`google-search-console/brands/${brand.id}/movers`)
      .then(setMovers)
      .catch(() => setMovers(null));
  }, [brand?.id, brand?.gscSiteUrl]);

  // Chart rows. Days Google reported nothing for are simply absent from the response — plotting
  // them as zero would draw a cliff where there is only silence.
  const series = useMemo(() => {
    const source = selectedKeyword ? data?.keywordDays : data?.days;
    if (!source?.length) return [];
    return source.map((d) => ({
      date: new Date(d.date).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
      clicks: d.clicks,
      impressions: d.impressions,
      position: reportedPosition(d),
    }));
  }, [data, selectedKeyword]);

  const totals = useMemo(() => {
    const source = selectedKeyword ? data?.keywordDays : data?.days;
    if (!source?.length) return null;
    const clicks = source.reduce((n, d) => n + (d.clicks ?? 0), 0);
    const impressions = source.reduce((n, d) => n + (d.impressions ?? 0), 0);
    return {
      clicks,
      impressions,
      // Impression-weighted, and only over days that actually reported a rank. A zero
      // position is missing data — weighting it in would treat silence as page one.
      position: (() => {
        const ranked = source.filter((d) => reportedPosition(d) != null);
        const weight = ranked.reduce((n, d) => n + (d.impressions ?? 0), 0);
        return weight
          ? ranked.reduce((n, d) => n + d.position * d.impressions, 0) / weight
          : null;
      })(),
      ctr: impressions ? (clicks / impressions) * 100 : null,
    };
  }, [data, selectedKeyword]);

  if (!brand?.gscSiteUrl) {
    return (
      <Container size="sm" mt="xl">
        <Paper withBorder p="xl" radius="md" ta="center">
          <Stack align="center" gap="sm">
            <ThemeIcon size={48} radius="xl" variant="light" color="gray">
              <IconSearch size={24} />
            </ThemeIcon>
            <Title order={4}>Search Console isn't linked</Title>
            <Text size="sm" c="dimmed">
              Link a Search Console property and we'll pull the last 16 months of daily data,
              then keep it current.
            </Text>
            <Button onClick={() => setLocation("/settings?tab=search-console")}>
              Link a property
            </Button>
          </Stack>
        </Paper>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Stack gap="md" mt="md">
        <Group justify="space-between" wrap="wrap">
          <Group gap="xs">
            <ThemeIcon size="sm" variant="light" color="blue">
              <IconSearch size={14} />
            </ThemeIcon>
            <Title order={3}>Search Console</Title>
            <Text size="sm" c="dimmed">{brand.gscSiteUrl.replace(/^sc-domain:/, "")}</Text>
          </Group>
          <Group gap="sm">
            <SegmentedControl size="xs" value={view} onChange={setView}
              data={Object.entries(VIEWS).map(([value, v]) => ({ value, label: v.label }))} />
            <SegmentedControl size="xs" value={days} onChange={setDays} data={RANGES} />
          </Group>
        </Group>

        {selectedKeyword && (
          <Group gap="xs">
            <Text size="sm" c="dimmed">Showing</Text>
            <Badge variant="light" size="lg">{selectedKeyword}</Badge>
            <Anchor size="sm" onClick={() => setSelectedKeyword(null)}>Back to the whole site</Anchor>
          </Group>
        )}

        {loading ? (
          <Center py="xl"><BeaconLoader size={40} /></Center>
        ) : series.length === 0 ? (
          <Alert icon={<IconInfoCircle size={16} />} color="blue">
            No Search Console data for this period yet. History is fetched in the background when a
            property is first linked — a full 16 months takes a few minutes to arrive.
          </Alert>
        ) : (
          <>
            {totals && (
              <Group gap="xl">
                <Metric label="Clicks" value={totals.clicks.toLocaleString()} />
                <Metric label="Impressions" value={totals.impressions.toLocaleString()} />
                <Metric label="CTR" value={totals.ctr != null ? `${totals.ctr.toFixed(1)}%` : "—"} />
                <Metric
                  label="Avg position"
                  value={formatPosition(totals.position)}
                />
              </Group>
            )}

            <Paper withBorder p="md" radius="md">
              <LineChart
                h={320}
                data={series}
                dataKey="date"
                series={VIEWS[view].series}
                curveType="monotone"
                withDots={false}
                withLegend={VIEWS[view].series.length > 1}
                // Position is a rank: 1 is the top of page one, so the axis reads better inverted.
                yAxisProps={VIEWS[view].reversed ? { reversed: true } : undefined}
                tickLine="x"
              />
            </Paper>

            <Text size="xs" c="dimmed">
              Site-wide totals straight from Search Console. These are larger than the figures in the
              weekly digest, which only counts your top 1,000 queries — the difference is the long tail.
            </Text>
          </>
        )}

        {movers?.movers?.length > 0 && (
          <Paper withBorder p="md" radius="md">
            <Stack gap="sm">
              <Group gap="xs">
                <IconTrendingUp size={18} />
                <Text fw={600}>What moved</Text>
              </Group>
              <Stack gap="xs">
                {movers.movers.map((m) => (
                  <Group key={m.keyword} gap="sm" wrap="nowrap" align="flex-start">
                    <Badge
                      size="sm"
                      variant="light"
                      color={m.impressionsWithoutClicks ? "orange" : m.isSpike ? "teal" : "red"}
                    >
                      {m.isSpike ? "up" : "down"}
                    </Badge>
                    <Anchor size="sm" onClick={() => setSelectedKeyword(m.keyword)}>
                      {m.summary}
                    </Anchor>
                  </Group>
                ))}
              </Stack>
              {/* Say what the comparison is against, so nobody reads the movement as a percentage
                  of anything or as a comparison with other brands. */}
              <Text size="xs" c="dimmed">{movers.method}</Text>
            </Stack>
          </Paper>
        )}

        {keywords?.topKeywords?.length > 0 && (
          <Paper withBorder p="md" radius="md">
            <Stack gap="sm">
              <Text fw={600}>Top keywords</Text>
              <Text size="xs" c="dimmed">Pick one to chart it on its own.</Text>
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Keyword</Table.Th>
                    <Table.Th ta="right">Clicks</Table.Th>
                    <Table.Th ta="right">Impressions</Table.Th>
                    <Table.Th ta="right">Position</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {keywords.topKeywords.map((k) => (
                    <Table.Tr
                      key={k.keyword}
                      style={{ cursor: "pointer" }}
                      onClick={() => setSelectedKeyword(k.keyword)}
                    >
                      <Table.Td>
                        <Anchor size="sm">{k.keyword}</Anchor>
                      </Table.Td>
                      <Table.Td ta="right">{k.clicks ?? 0}</Table.Td>
                      <Table.Td ta="right">{k.impressions ?? 0}</Table.Td>
                      <Table.Td ta="right">{formatPosition(k.position)}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Stack>
          </Paper>
        )}
      </Stack>
    </Container>
  );
}

function Metric({ label, value }) {
  return (
    <Stack gap={2}>
      <Text size="xs" c="dimmed">{label}</Text>
      <Text size="xl" fw={700}>{value}</Text>
    </Stack>
  );
}
